import { classnames } from 'Utils/classnames/classnames.utils';
import { useRouter } from 'next/router';
import React from 'react';
import URI from 'urijs';
import { b2cPagesService, constants } from '@clubmed/utils';
import { ICONS } from 'Components/utils/icon/icons';
import { usePageContext } from 'Context/PageContext';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
type BreadcrumbLineProps = {
  countryPage?: null | {
    pageCountry: {
      breadcrumbLabel: string;
    };
    url: string;
  };
  originalUrl: string;
  regionPage?: null | {
    pageRegion: {
      breadcrumbLabel: string;
    };
    url: string;
  };
  resort?: {
    name: string;
  };
};
type BreadcrumbCellProps = {
  className: string;
  isCurrent: boolean;
  label: string;
  url?: string;
};

const BreadcrumbCell = ({ className, isCurrent, label, url }: BreadcrumbCellProps) =>
  url ? (
    <DynamicLink
      className={classnames('underline', className)}
      url={url}
      {...(isCurrent
        ? {
            'aria-current': 'location',
          }
        : null)}
    >
      <span className="max-w-full truncate">{label}</span>
    </DynamicLink>
  ) : (
    <span className={classnames('font-bold', className)}>
      <span className="max-w-full truncate">{label}</span>
    </span>
  );

export const ProductBreadcrumbLine = ({
  countryPage,
  originalUrl,
  regionPage,
  resort,
}: BreadcrumbLineProps) => {
  const { breadcrumb, pageRegion, pageCountry, pageType } = usePageContext();
  const { asPath } = useRouter();
  const origin = new URI(originalUrl).origin().toString();
  const {
    PAGE_TYPES: {
      DESTINATION,
      DESTINATION_CRUISE,
      DESTINATION_TOUR,
      DESTINATION_TRAVEL,
      LANDING,
      LANDING_MICE,
      OFFER,
      RESORT: RESORT_PAGE,
    },
  } = b2cPagesService;
  const breadcrumbItems = [
    {
      label: constants.WEBSITE_NAME,
      url: '/',
    },
  ];

  switch (pageType) {
    case LANDING:
    case LANDING_MICE:
    case OFFER:
      if (breadcrumb) {
        breadcrumbItems.push(...breadcrumb);
      }

      break;

    case DESTINATION:
    case DESTINATION_CRUISE:
    case DESTINATION_TOUR:
    case DESTINATION_TRAVEL:
      const destinationItems = [
        regionPage?.pageRegion
          ? {
              label: regionPage.pageRegion.breadcrumbLabel,
              url: regionPage.url,
            }
          : null,
        pageRegion && pageCountry
          ? {
              label: pageCountry.breadcrumbLabel,
              url: '',
            }
          : null,
        pageRegion && !pageCountry
          ? {
              label: pageRegion.breadcrumbLabel,
              url: '',
            }
          : null,
      ];
      breadcrumbItems.push(...destinationItems.filter(Boolean));
      break;

    case RESORT_PAGE:
      const resortItems = [
        regionPage?.pageRegion
          ? {
              label: regionPage.pageRegion.breadcrumbLabel,
              url: regionPage.url,
            }
          : null,
        countryPage?.pageCountry
          ? {
              label: countryPage.pageCountry.breadcrumbLabel,
              url: countryPage.url,
            }
          : null,
        resort
          ? {
              label: resort.name,
              url: '',
            }
          : null,
      ];
      breadcrumbItems.push(...resortItems.filter(Boolean));
      break;

    default:
  }

  if (breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <SiteContainer className="Line Line-Breadcrumb">
      {[
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems.map((item, index) => {
              return {
                '@type': 'ListItem',
                position: index + 1,
                item: {
                  '@id': item.url
                    ? encodeURIComponent(origin + item.url)
                    : encodeURIComponent(new URI(origin + asPath).readable()),
                  name: item.label,
                },
              };
            }),
          }),
        },
      ].map((script, idx) => (
        <script
          key={idx}
          type={script.type}
          dangerouslySetInnerHTML={{
            __html: script.innerHTML,
          }}
        />
      ))}
      <nav
        aria-label="breadcrumbs"
        className="scroll-x scrollbar-hidden flex items-end sm:overflow-x-visible"
        data-testid="breadcrumb"
      >
        <Icon className="shrink-0" name={ICONS.HOME} width="0.75rem" />
        <ol className="flex items-center gap-5 pe-20 leading-1 sm:pe-0">
          {breadcrumbItems.map((item, i) => (
            <li className="flex items-center gap-5" key={i}>
              <BreadcrumbCell
                {...item}
                className="whitespace-nowrap text-12 text-black"
                isCurrent={i === breadcrumbItems.length - 1}
              />
              {i < breadcrumbItems.length - 1 && (
                <span className="leading-0" aria-hidden="true">
                  &gt;
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </SiteContainer>
  );
};
